/**
 * FramesTableBase — универсальный компонент таблицы кадров.
 *
 * Теперь фильтр по сменам работает на сервере через параметр `work_shifts`.
 * 
 * Пропсы:
 *   - columns, storageKey, frames, selectedFrame, onSelectFrame, isInitialLoading
 *   - filters: объект с текущими серверными фильтрами (включает work_shifts)
 *   - onApplyFilter: функция для обновления фильтров (вызывает перезапрос)
 *   - shifts: массив смен камеры [{start, end}, ...] (для отображения смены в ячейке и для списка доступных смен)
 *   - currentPage, pageSize, totalPages, onPageChange, onPageSizeChange
 *   - totalItems: общее количество записей (опционально, для отображения в пагинации)
 *   - gantt: диаграмма кадров (ТЗ V5 №16). С ней фильтры выносятся над обоими
 *     блоками, а диаграмма и таблица сворачиваются независимо: по умолчанию
 *     диаграмма открыта, таблица свёрнута.
 */
import React, { useRef, useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp, Funnel, Gear } from "@gravity-ui/icons";
import { DateTimeInput } from "../DateTimeInput";
import { formatInTimezone, getPartsInTimezone } from "../../utils/formatInTimezone";
import { describeSingleTimezone } from "../../utils/cameraTimezone";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import ColumnResizeHandle from "../ColumnResizeHandle/ColumnResizeHandle";
import "./FramesTableBase.css";

const PEOPLE_MAX = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

// ─── Утилиты ────────────────────────────────────────────────────────────────

/** ТЗ №2 §10.3: camera-bound timestamp — в часовом поясе камеры, не браузера. */
export const formatDateTime = (iso, timezone) => {
  if (!iso) return "—";
  return formatInTimezone(iso, timezone);
};

const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

/** Смена определяется по локальному времени камеры (ТЗ №2 §10.3), не браузера. */
export const getShiftByTime = (iso, shiftsArray, timezone) => {
  if (!iso || !shiftsArray || shiftsArray.length === 0) return "—";
  const parts = getPartsInTimezone(iso, timezone);
  if (!parts) return "—";
  const minutes = parts.hour * 60 + parts.minute;

  for (let i = 0; i < shiftsArray.length; i++) {
    const shift = shiftsArray[i];
    if (!shift.start || !shift.end) continue;
    const startMin = timeToMinutes(shift.start);
    const endMin = timeToMinutes(shift.end);
    if (startMin === null || endMin === null) continue;

    if (endMin > startMin) {
      if (minutes >= startMin && minutes < endMin) return `Смена ${i + 1}`;
    } else {
      if (minutes >= startMin || minutes < endMin) return `Смена ${i + 1}`;
    }
  }
  return "—";
};

// Преобразование меток смен (например, "Смена 1") в enum WorkShift
const mapShiftLabelsToEnums = (labels) => {
  return labels.map(label => {
    const match = label.match(/Смена (\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      return `SHIFT_${num}`;
    }
    return null;
  }).filter(Boolean);
};

// ─── Позиционирование попапа ─────────────────────────────────────────────────
const usePopupPosition = (popupRef, anchorRef, isOpen) => {
  useEffect(() => {
    if (!isOpen || !popupRef.current || !anchorRef.current) return;
    const popup = popupRef.current;
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();

    let top = anchorRect.bottom + 4;
    let left = anchorRect.left;

    if (left + popupRect.width > window.innerWidth) left = window.innerWidth - popupRect.width - 8;
    if (left < 0) left = 8;
    if (top + popupRect.height > window.innerHeight) top = anchorRect.top - popupRect.height - 4;
    if (top < 52) top = 52;

    popup.style.top = `${top}px`;
    popup.style.left = `${left}px`;
  }, [isOpen, popupRef, anchorRef]);
};

// Хук закрытия попапа по клику снаружи
const useClickOutside = (popupRef, anchorRef, callback) => {
  useEffect(() => {
    const handler = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        callback();
      }
    };
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handler); };
  }, [popupRef, anchorRef, callback]);
};

// ─── DateFilterPopup ─────────────────────────────────────────────────────────
const DateFilterPopup = ({ anchorRef, filters, onApply, onClose, timezone }) => {
  const [start, setStart] = useState(filters.start_date || "");
  const [end, setEnd] = useState(filters.end_date || "");
  const popupRef = useRef(null);

  useEffect(() => {
    setStart(filters.start_date || "");
    setEnd(filters.end_date || "");
  }, [filters]);

  const apply = useCallback(() => { onApply({ start_date: start, end_date: end }); onClose(); }, [start, end, onApply, onClose]);
  const clear = useCallback(() => { onApply({ start_date: "", end_date: "" }); onClose(); }, [onApply, onClose]);

  useClickOutside(popupRef, anchorRef, apply);
  usePopupPosition(popupRef, anchorRef, true);

  return (
    <div ref={popupRef} className="fbt-popup">
      <div className="fbt-popup-title">Фильтр по дате</div>
      <div className="fbt-popup-note">{describeSingleTimezone(timezone)}</div>
      <label className="fbt-field-label">
        От
        <DateTimeInput className="fbt-input" value={start} onChange={setStart} onEnter={apply} ariaLabel="Дата от" />
      </label>
      <label className="fbt-field-label">
        До
        <DateTimeInput className="fbt-input" value={end} onChange={setEnd} onEnter={apply} ariaLabel="Дата до" />
      </label>
      <div className="fbt-popup-actions">
        <button className="fbt-apply-btn" onClick={apply}>Применить</button>
        {(start || end) && <button className="fbt-clear-btn" onClick={clear}>Сбросить</button>}
      </div>
    </div>
  );
};

// ─── ShiftFilterPopup ────────────────────────────────────────────────────────
const ShiftFilterPopup = ({ anchorRef, availableShifts, currentWorkShifts, onApply, onClose }) => {
  // currentWorkShifts — массив enum-значений из filters.work_shifts
  const [selected, setSelected] = useState(() => {
    if (!currentWorkShifts) return [];
    // преобразуем enum "SHIFT_1" обратно в "Смена 1" для отображения
    return currentWorkShifts.map(shiftEnum => {
      const num = shiftEnum.split('_')[1];
      return `Смена ${num}`;
    }).filter(label => availableShifts.includes(label));
  });
  const popupRef = useRef(null);

  const toggle = (shift) =>
    setSelected(prev => prev.includes(shift) ? prev.filter(s => s !== shift) : [...prev, shift]);

  const apply = useCallback(() => {
    // преобразуем выбранные метки в enum и отправляем на сервер
    const enums = mapShiftLabelsToEnums(selected);
    onApply({ work_shifts: enums });
    onClose();
  }, [selected, onApply, onClose]);

  const clear = useCallback(() => {
    onApply({ work_shifts: [] });
    onClose();
  }, [onApply, onClose]);

  useClickOutside(popupRef, anchorRef, apply);
  usePopupPosition(popupRef, anchorRef, true);

  return (
    <div ref={popupRef} className="fbt-popup">
      <div className="fbt-popup-title">Фильтр по сменам</div>
      <div className="fbt-check-list">
        {availableShifts.map(shift => (
          <label key={shift} className="fbt-check-item">
            <input type="checkbox"
              checked={selected.includes(shift)}
              onChange={() => toggle(shift)} />
            {shift}
          </label>
        ))}
      </div>
      <div className="fbt-popup-actions">
        <button className="fbt-apply-btn" onClick={apply}>Применить</button>
        {selected.length > 0 && <button className="fbt-clear-btn" onClick={clear}>Сбросить</button>}
      </div>
    </div>
  );
};

// ─── PeopleRangeFilterPopup (без изменений) ─────────────────────────────────
const PeopleRangeFilterPopup = ({ anchorRef, filters, onApply, onClose }) => {
  const initMin = filters.people_count_min !== "" ? Number(filters.people_count_min) : 0;
  const initMax = filters.people_count_max !== "" ? Number(filters.people_count_max) : PEOPLE_MAX;

  const [minVal, setMinVal] = useState(initMin);
  const [maxVal, setMaxVal] = useState(initMax);
  const [error, setError] = useState("");
  const popupRef = useRef(null);

  useEffect(() => {
    setMinVal(filters.people_count_min !== "" ? Number(filters.people_count_min) : 0);
    setMaxVal(filters.people_count_max !== "" ? Number(filters.people_count_max) : PEOPLE_MAX);
  }, [filters]);

  const clamp = (v) => Math.max(0, Math.min(PEOPLE_MAX, isNaN(Number(v)) ? 0 : Number(v)));
  const validate = (mn, mx) => (mn > mx ? "Минимум не может быть больше максимума" : "");

  const apply = useCallback(() => {
    const err = validate(minVal, maxVal);
    if (err) { setError(err); return; }
    onApply({ people_count_min: String(minVal), people_count_max: String(maxVal) });
    onClose();
  }, [minVal, maxVal, onApply, onClose]);

  const clear = useCallback(() => {
    onApply({ people_count_min: "", people_count_max: "" });
    onClose();
  }, [onApply, onClose]);

  useClickOutside(popupRef, anchorRef, apply);
  usePopupPosition(popupRef, anchorRef, true);

  const minPct = (minVal / PEOPLE_MAX) * 100;
  const maxPct = (maxVal / PEOPLE_MAX) * 100;

  return (
    <div ref={popupRef} className="fbt-popup">
      <div className="fbt-popup-title">Фильтр по количеству людей</div>
      <div className="fbt-slider-wrap">
        <div className="fbt-slider-track" />
        <div className="fbt-slider-fill" style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }} />
        <input className="fbt-range" type="range" min={0} max={PEOPLE_MAX} value={minVal}
          onChange={e => { const v = clamp(e.target.value); setMinVal(v); setError(validate(v, maxVal)); }}
          onMouseUp={apply} onTouchEnd={apply} />
        <input className="fbt-range" type="range" min={0} max={PEOPLE_MAX} value={maxVal}
          onChange={e => { const v = clamp(e.target.value); setMaxVal(v); setError(validate(minVal, v)); }}
          onMouseUp={apply} onTouchEnd={apply} />
      </div>
      <div className="fbt-range-inputs">
        <div className="fbt-range-field">
          <span className="fbt-range-label">От</span>
          <input type="number" className={`fbt-num-input${error ? " fbt-num-input--error" : ""}`}
            value={minVal} min={0} max={PEOPLE_MAX}
            onChange={e => { const v = clamp(e.target.value); setMinVal(v); setError(validate(v, maxVal)); }}
            onBlur={apply} onKeyDown={e => e.key === "Enter" && apply()} />
        </div>
        <span className="fbt-range-sep">—</span>
        <div className="fbt-range-field">
          <span className="fbt-range-label">До</span>
          <input type="number" className={`fbt-num-input${error ? " fbt-num-input--error" : ""}`}
            value={maxVal} min={0} max={PEOPLE_MAX}
            onChange={e => { const v = clamp(e.target.value); setMaxVal(v); setError(validate(minVal, v)); }}
            onBlur={apply} onKeyDown={e => e.key === "Enter" && apply()} />
        </div>
      </div>
      {error && <div className="fbt-error">{error}</div>}
      <div className="fbt-popup-actions">
        <button className="fbt-apply-btn" onClick={apply}>Применить</button>
        {(filters.people_count_min !== "" || filters.people_count_max !== "") &&
          <button className="fbt-clear-btn" onClick={clear}>Сбросить</button>}
      </div>
    </div>
  );
};

// ─── TextFilterPopup ─────────────────────────────────────────────────────────
const TextFilterPopup = ({ anchorRef, title, filterKey, filters, onApply, onClose }) => {
  const [value, setValue] = useState(filters[filterKey] || "");
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 0); }, []);

  const apply = useCallback(() => { onApply({ [filterKey]: value }); onClose(); }, [filterKey, value, onApply, onClose]);
  const clear = useCallback(() => { onApply({ [filterKey]: "" }); onClose(); }, [filterKey, onApply, onClose]);

  useClickOutside(popupRef, anchorRef, apply);
  usePopupPosition(popupRef, anchorRef, true);

  return (
    <div ref={popupRef} className="fbt-popup">
      {title && <div className="fbt-popup-title">{title}</div>}
      <input ref={inputRef} type="text" className="fbt-input" value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => e.key === "Enter" && apply()}
        placeholder="Введите текст" />
      <div className="fbt-popup-actions">
        <button className="fbt-apply-btn" onClick={apply}>Применить</button>
        {filters[filterKey] && <button className="fbt-clear-btn" onClick={clear}>Сбросить</button>}
      </div>
    </div>
  );
};

// ─── ColumnSettingsPopup ─────────────────────────────────────────────────────
const ColumnSettingsPopup = ({ anchorRef, columns, visibleColumns, onToggle, onClose }) => {
  const popupRef = useRef(null);

  useClickOutside(popupRef, anchorRef, onClose);
  usePopupPosition(popupRef, anchorRef, true);

  const hideableColumns = columns.filter(c => c.hideable !== false);

  return (
    <div ref={popupRef} className="fbt-popup">
      <div className="fbt-popup-title">Настройка колонок</div>
      <div className="fbt-check-list">
        {hideableColumns.map(col => (
          <label key={col.key} className="fbt-check-item">
            <input type="checkbox"
              checked={visibleColumns[col.key] ?? col.defaultVisible ?? true}
              onChange={() => onToggle(col.key)} />
            {col.label}
          </label>
        ))}
      </div>
    </div>
  );
};

// ─── PaginationControls ──────────────────────────────────────────────────────
const PaginationControls = ({ currentPage, totalPages, pageSize, onPageChange, onPageSizeChange, totalItems }) => {
  const safeTotalPages = totalPages || 1;
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(safeTotalPages, currentPage + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="fbt-pagination">
      <div className="fbt-pagination-size">
        <span>На странице:</span>
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
          {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {safeTotalPages > 1 && (
        <div className="fbt-pagination-pages">
          <button className="fbt-page-btn" onClick={() => onPageChange(1)} disabled={currentPage === 1}>«</button>
          <button className="fbt-page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
          {start > 1 && <span className="fbt-page-ellipsis">…</span>}
          {pages.map(p => (
            <button key={p}
              className={`fbt-page-btn${p === currentPage ? " fbt-page-btn--active" : ""}`}
              onClick={() => onPageChange(p)}>{p}</button>
          ))}
          {end < safeTotalPages && <span className="fbt-page-ellipsis">…</span>}
          <button className="fbt-page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === safeTotalPages}>›</button>
          <button className="fbt-page-btn" onClick={() => onPageChange(safeTotalPages)} disabled={currentPage === safeTotalPages}>»</button>
        </div>
      )}
      <span className="fbt-pagination-info">
        {totalItems > 0
          ? `Стр. ${currentPage} / ${safeTotalPages} (${totalItems} записей)`
          : `Стр. ${currentPage}${safeTotalPages > 1 ? ` / ${safeTotalPages}` : ""}`}
      </span>
    </div>
  );
};

// ─── Основной компонент ──────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {Array}  props.columns
 * @param {string} props.storageKey
 * @param {Array}  props.frames              — данные с сервера (уже отфильтрованы)
 * @param {object} props.selectedFrame
 * @param {function} props.onSelectFrame
 * @param {boolean} props.isInitialLoading
 * @param {object} props.filters             — серверные фильтры (включая work_shifts)
 * @param {function} props.onApplyFilter     — обновляет фильтры (вызывает запрос)
 * @param {Array}  props.shifts              — смены из камеры [{start, end}, ...]
 * @param {number} props.currentPage
 * @param {number} props.pageSize
 * @param {number} props.totalPages
 * @param {function} props.onPageChange
 * @param {function} props.onPageSizeChange
 * @param {number} props.totalItems          — общее количество записей (опционально)
 */
const FramesTableBase = ({
  columns = [],
  storageKey = "framesTableBase_columns",
  frames = [],
  selectedFrame,
  onSelectFrame,
  isInitialLoading,
  filters = {},
  onApplyFilter,
  shifts = [],
  currentPage = 1,
  pageSize = 50,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  totalItems, // опционально
  timezone, // Camera.display_timezone — для дат и смен (ТЗ №2 §10.3)
  gantt = null,
}) => {
  const [ganttOpen, setGanttOpen] = useState(true);
  const [tableOpen, setTableOpen] = useState(false);
  const barRefs = useRef({});

  // ── Видимость колонок (без изменений) ─────────────────────────────────────
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try { return JSON.parse(saved); } catch { }
    }
    return Object.fromEntries(columns.map(c => [c.key, c.defaultVisible ?? true]));
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
  }, [storageKey, visibleColumns]);

  const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Ширина колонок: тянется за край шапки ─────────────────────────────────
  const resize = useResizableColumns(`${storageKey}_widths`);

  // ── Открытый попап ────────────────────────────────────────────────────────
  const [openPopup, setOpenPopup] = useState(null);
  const anchorRefs = useRef({});
  const colSettingsRef = useRef(null);
  columns.forEach(col => {
    if (!anchorRefs.current[col.key]) anchorRefs.current[col.key] = React.createRef();
  });

  // ── Доступные смены для фильтра (берём из shifts) ────────────────────────
  const availableShifts = React.useMemo(
    () => shifts.map((_, i) => `Смена ${i + 1}`),
    [shifts]
  );

  // ── Активность фильтров (обновлено для смен) ────────────────────────────
  const isFilterActive = (col) => {
    switch (col.filterType) {
      case "date": return !!(filters.start_date || filters.end_date);
      case "shift": return filters.work_shifts && filters.work_shifts.length > 0;
      case "people": return filters.people_count_min !== "" || filters.people_count_max !== "";
      case "text": return !!filters[col.filterKey];
      default: return false;
    }
  };

  const handleApply = (values) => { onApplyFilter(values); setOpenPopup(null); };

  // ── Рендер заголовка колонки (без изменений) ──────────────────────────────
  const renderTh = (col) => {
    if (visibleColumns[col.key] === false) return null;
    const hasFilter = !!col.filterType;
    const active = isFilterActive(col);
    if (!anchorRefs.current[col.key]) anchorRefs.current[col.key] = React.createRef();

    return (
      <th key={col.key} data-col-key={col.key}>
        <div className="fbt-th-inner">
          {col.label}
          {hasFilter && (
            <button
              ref={anchorRefs.current[col.key]}
              className={`fbt-filter-btn${active ? " fbt-filter-btn--active" : ""}`}
              onClick={() => setOpenPopup(prev => prev === col.key ? null : col.key)}
              title={`Фильтр по столбцу «${col.label}»`}
              aria-label={`Фильтр по столбцу «${col.label}»`}><ChevronDown width={12} height={12} /></button>
          )}
        </div>
        <ColumnResizeHandle columnKey={col.key} resize={resize} />
      </th>
    );
  };

  // ── Рендер попапа (для смен теперь передаём work_shifts) ─────────────────
  const renderPopup = (col) => {
    const fromBar = openPopup === `bar:${col.key}`;
    if (openPopup !== col.key && !fromBar) return null;
    const anchor = fromBar ? barRefs.current[col.key] : anchorRefs.current[col.key];

    switch (col.filterType) {
      case "date":
        return (
          <DateFilterPopup key={col.key}
            anchorRef={anchor} filters={filters} timezone={timezone}
            onApply={handleApply} onClose={() => setOpenPopup(null)} />
        );
      case "shift":
        return (
          <ShiftFilterPopup key={col.key}
            anchorRef={anchor}
            availableShifts={availableShifts}
            currentWorkShifts={filters.work_shifts || []}
            onApply={handleApply}
            onClose={() => setOpenPopup(null)} />
        );
      case "people":
        return (
          <PeopleRangeFilterPopup key={col.key}
            anchorRef={anchor} filters={filters}
            onApply={handleApply} onClose={() => setOpenPopup(null)} />
        );
      case "text":
        return (
          <TextFilterPopup key={col.key}
            anchorRef={anchor}
            title={`Фильтр по столбцу «${col.label}»`}
            filterKey={col.filterKey}
            filters={filters}
            onApply={handleApply}
            onClose={() => setOpenPopup(null)} />
        );
      default:
        return null;
    }
  };

  const visibleKeys = columns.filter(c => visibleColumns[c.key] !== false).map(c => c.key);
  const visibleCount = visibleKeys.length;
  // используем переданный totalItems или длину текущей страницы как fallback
  const totalItemsToShow = totalItems !== undefined ? totalItems : frames.length;

  const filterColumns = columns.filter(col => col.filterType);
  const renderFilterBar = () => (
    <div className="fbt-filterbar" role="group" aria-label="Фильтры кадров">
      <Funnel width={16} height={16} className="fbt-filterbar-icon" aria-hidden="true" />
      {filterColumns.map(col => {
        if (!barRefs.current[col.key]) barRefs.current[col.key] = React.createRef();
        const active = isFilterActive(col);
        return (
          <button key={col.key} type="button" ref={barRefs.current[col.key]}
            className={`fbt-filterbar-btn${active ? " fbt-filterbar-btn--active" : ""}`}
            aria-pressed={active}
            onClick={() => setOpenPopup(prev => prev === `bar:${col.key}` ? null : `bar:${col.key}`)}>
            {col.label}
            <ChevronDown width={12} height={12} />
          </button>
        );
      })}
    </div>
  );

  const renderSectionHeader = (title, open, setOpen) => (
    <button type="button" className="fbt-section-toggle" aria-expanded={open} onClick={() => setOpen(v => !v)}>
      {open ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
      {title}
    </button>
  );

  const showTable = !gantt || tableOpen;

  return (
    <div className={`fbt-list${gantt ? " fbt-list--gantt" : ""}`}>
      {gantt && (
        <>
          {renderFilterBar()}
          <section className="fbt-section">
            {renderSectionHeader("Диаграмма", ganttOpen, setGanttOpen)}
            {ganttOpen && <div className="fbt-section-body">{gantt}</div>}
          </section>
          <section className="fbt-section">
            {renderSectionHeader("Таблица", tableOpen, setTableOpen)}
          </section>
        </>
      )}
      {showTable && (<>
      <div className="fbt-toolbar">
        {/* FE-FIX10: времена в таблице отображаются в поясе камеры — говорим об
            этом явно, а не оставляем пользователя гадать. */}
        <span className="fbt-tz">{describeSingleTimezone(timezone)}</span>
        <button ref={colSettingsRef} className="fbt-col-settings-btn"
          onClick={() => setOpenPopup(prev => prev === "colSettings" ? null : "colSettings")}
          title="Настройка колонок"><Gear width={16} height={16} /></button>
      </div>

      <div className="fbt-wrapper">
        <table
          className={`fbt-table${resize.isResized ? " fbt-table--resized" : ""}`}
          style={resize.isResized ? { width: resize.tableWidth(visibleKeys) } : undefined}>
          {resize.isResized && (
            <colgroup>
              {visibleKeys.map(key => <col key={key} style={{ width: resize.columnWidth(key) }} />)}
            </colgroup>
          )}
          <thead>
            <tr>{columns.map(renderTh)}</tr>
          </thead>
          <tbody>
            {isInitialLoading ? (
              <tr><td colSpan={visibleCount} style={{ textAlign: "center", padding: 20 }}>Загрузка…</td></tr>
            ) : frames.length === 0 ? (
              <tr><td colSpan={visibleCount} style={{ textAlign: "center", padding: 20, color: "#999" }}>Нет данных</td></tr>
            ) : frames.map(frame => (
              <tr key={frame.id}
                className={selectedFrame?.id === frame.id ? "fbt-selected" : ""}
                onClick={() => onSelectFrame(frame)}>
                {columns.map(col => {
                  if (visibleColumns[col.key] === false) return null;
                  return <td key={col.key}>{col.render(frame, shifts, timezone)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        totalItems={totalItemsToShow}
      />
      </>)}

      {columns.map(renderPopup)}
      {openPopup === "colSettings" && (
        <ColumnSettingsPopup
          anchorRef={colSettingsRef}
          columns={columns}
          visibleColumns={visibleColumns}
          onToggle={toggleColumn}
          onClose={() => setOpenPopup(null)} />
      )}
    </div>
  );
};

export default FramesTableBase;